/**
 * Restaurant POS resource — sellable items only:
 * - menu dishes (always)
 * - stock ingredients with sellOnPos === true and qty > 0
 *
 * Kitchen-only ingredients stay in Inventaire / Cuisine / includedStockItems,
 * but never appear in POS browse/search.
 *
 * Role predicates are local (same rules as restaurantMode) to avoid import cycles.
 */

const PRODUCT_ROLE_RESTAURANT_DISH = 'restaurant_dish';
const PRODUCT_ROLE_INGREDIENT = 'ingredient';

/** Tab QuickSale pour articles achetés volontairement vendus (Coca, sauce, etc.) */
export const RESTAURANT_POS_STOCK_SECTION = 'stock-articles';

function isDish(product) {
  if (!product) return false;
  return (
    product.productRole === PRODUCT_ROLE_RESTAURANT_DISH ||
    product.isRestaurantDish === true
  );
}

function isIngredient(product) {
  if (!product || isDish(product)) return false;
  if (product.productRole === PRODUCT_ROLE_INGREDIENT) return true;
  return !product.productRole;
}

/**
 * Ingredient explicitly offered for POS sale (opt-in).
 * Missing / false → kitchen-only.
 */
export function isRestaurantSellableStockProduct(product) {
  if (!isIngredient(product)) return false;
  return product.sellOnPos === true;
}

/** Anything that belongs in the restaurant POS catalog (ignores stock qty). */
export function isRestaurantPosResource(product) {
  if (!product) return false;
  return isDish(product) || isRestaurantSellableStockProduct(product);
}

/**
 * QuickSale restaurant: plats menu + sellable stock with qty > 0.
 * @param {object[]} products
 * @param {(product: object) => number} getInventory
 */
export function getRestaurantPosSaleProducts(products, getInventory = () => 0) {
  const list = products || [];
  const dishes = list.filter(isDish);
  const stocked = list.filter((p) => {
    if (!isRestaurantSellableStockProduct(p)) return false;
    return getInventory(p) > 0;
  });
  const byId = new Map();
  for (const p of [...dishes, ...stocked]) {
    const id = p?.id ?? p?.productId;
    if (id != null) byId.set(String(id), p);
  }
  return Array.from(byId.values());
}
